import { Popover } from "antd"
import { FC, useEffect, useMemo, useState } from "react"
import { useHistory } from "react-router-dom"

import { Button, Skeleton } from "@/components"
import PageLayout from "@/components/Layout"
import Progress from "@/components/Progress"
import Select from "@/components/Select"
import requests, { Amount, ChainItem } from "@/requests/requests"
import usePay from "@/store/payment"
import useScreenLoading from "@/store/screenLoading"
import { AlignCenter, Between } from "@/styles/CssElement"

import { useCountdown } from "./useCountdown"

import "./style.less"

const Page: FC = () => {
    const router = useHistory()
    const {
        order,
        goods,
        chains,
        chainId,
        setOrder,
        chainItem,
        setChainId,
        setChainItem,
        setLoopOrder
    } = usePay(s => ({
        order: s.order,
        goods: s.goods,
        chains: s.chains,
        chainId: s.chainId,
        setOrder: s.setOrder,
        chainItem: s.chainItem,
        setChainId: s.setChainId,
        setChainItem: s.setChainItem,
        setLoopOrder: s.setLoopOrder
    }))
    const [skeletonLoading, setSkeletonLoading] = useState<boolean>(false)
    const [time, start] = useCountdown()
    const [amounts, setAmounts] = useState<Record<string, Amount>>({})
    const currentChain = useMemo(() => {
        if (chainId) {
            return chains.find(({ chain }) => chain === chainId)
        }
    }, [chains, chainId])

    const getAmount = (params: string[]) => {
        setSkeletonLoading(true)
        requests.amount(params).then(r => {
            setAmounts(r)
            start(10)
        }).catch((err) => {
            if (err.code === 401) {
                router.push("/end" + router.location.pathname)
            }
        }).finally(() => {
            setTimeout(() => {
                setSkeletonLoading(false)
            }, 250)
        })
    }

    const { chainMap } = useMemo(() => {
        return {
            chainMap: chains.map(({ chain, logo }) => {

                return {
                    key: chain,
                    value: chain,
                    logo
                }
            })
        }
    }, [chains])

    const _setChainId = (chainId: string) => {
        setChainId(chainId)
    }

    const check = (item: ChainItem & { currency_id: string }) => {
        setChainItem(item)
    }

    const setScreenLoading = useScreenLoading(s => s.setScreenLoading)

    const pay = () => {
        setScreenLoading(true)
        requests.pay(chainItem!.currency_id.toString()).then(o => {
            setOrder(o)
            setLoopOrder(o)
            setScreenLoading(false)
        }).catch(() => {
            setScreenLoading(true)
        })
    }

    useEffect(() => {
        if (chains.length) {
            setChainId(chains[0].chain)
            setSkeletonLoading(true)
        }
    }, [chains])

    useEffect(() => {
        if (order.trade_no) {
            router.replace("/cashier" + router.location.pathname)
        }
    }, [order])

    useEffect(() => {
        if (time === 0 && currentChain?.subset) {
            getAmount(currentChain.subset.map(({ currency_id }) => currency_id))
        }
    }, [time, currentChain])

    useEffect(() => {
        if (currentChain?.subset) {
            setChainItem(null)
            getAmount(currentChain.subset.map(({ currency_id }) => currency_id))
        }
    }, [currentChain])

    useEffect(() => {
        currentChain?.subset && setChainItem(currentChain.subset[0])
    }, [currentChain])

    return (
        <PageLayout>
            <div className="header-wrapper">
                <AlignCenter className="default-header">
                    <img
                        className="default-header-icon"
                        src={goods.app_icon || require("@/assets/Merchant.png")}
                    />
                    <div className="default-header-title">{goods.app_name}</div>
                </AlignCenter>
                <div className="good">
                    <img className="goodImage" src={goods.image || require('@/assets/goodPlaceholder.jpg')} />
                    <div style={{ flex: 1, overflow: 'hidden', textAlign: 'left' }}>
                        <Popover placement="bottom" content={goods.name}>
                            <div className="goodTitle">{goods.name}</div>
                        </Popover>
                        <div className="goodAmount">{goods.amount} <span>{goods.symbol}</span></div>
                    </div>
                </div>
            </div>
            <Between style={{ alignItems: 'center', marginBottom: '18px' }}>
                <div className="selectTitle">Select Chain</div>
                <Progress size={34} percentage={time * 30}>
                    <span style={{ fontSize: 16, fontWeight: 600, lineHeight: '34px' }}>{time}s</span>
                </Progress>
            </Between>
            <Select
                small
                style={{ marginBottom: '20px' }}
                className="select"
                value={chainId}
                options={chainMap}
                onChange={(id: string) => { _setChainId(id) }}
            />
            <div className="payment-tokens">
                {currentChain?.subset?.map((item, index) => (
                    <Skeleton
                        // loading={skeletonLoading}
                        loading={false}
                        key={`${item.currency_id}_${index}`}
                        size={48}
                        className="tokenSkeleton"
                    >
                        <div
                            className={`payment-tokens-item${chainItem?.currency_id.toString() === item.currency_id ? " actived" : ""}`}
                            onClick={check.bind(void 0, item)}
                        >
                            <img className="payment-tokens-logo" src={item.logo} />
                            <div>{item.token}</div>
                        </div>
                    </Skeleton>
                ))}
            </div>
            <Skeleton
                loading={skeletonLoading}
                size={48}
                className="amountSkeleton"
            >
                <div className="payAmount">
                    {chainItem?.token}
                    <span> {chainItem?.currency_id ? amounts[chainItem?.currency_id]?.amount : ''}</span>
                </div>
            </Skeleton>
            <div className="paymentFooter">
                <Button
                    disabled={!(chainItem && !skeletonLoading)}
                    size="large"
                    type="primary"
                    onClick={pay}
                    className="btn"
                >
                    Checkout
                </Button>
                <div className="powerBy">Payments processed by ReelPay. Be sure you trust the merchant listed, as transactions are final and refunds are handled by the merchant, <a target="_blank" href="https://reelpay.puttest.com/policy/2">See full disclosures</a> and <a target="_blank" href="https://reelpay.puttest.com/policy/1">Privacy Policy</a></div>
            </div>
        </PageLayout >
    )
}

export default Page
